/**
 * Browser-side proof-of-work solver for the loginflow
 * PoW challenge. The server returns a random challenge
 * and a required difficulty (leading zero bits); the
 * client searches for a nonce such that
 * SHA-256(challenge || nonce) has at least `difficulty`
 * leading zero bits.
 *
 * # Performance posture
 *
 * The solver runs on the main thread by default. For a
 * difficulty of 18 bits this averages ~100-300ms on
 * modern laptops and ~200-500ms on phones — barely
 * perceptible and acceptable once per sign-in attempt.
 * Callers who want to hide the latency entirely can run
 * the solver inside a Web Worker; the function is
 * structured so the hot loop is pure math with no DOM
 * access.
 *
 * The nonce is incremented as an 8-byte big-endian
 * counter starting from a random seed so two solvers
 * racing on the same challenge don't duplicate work.
 * The solved nonce is returned base64url-encoded so the
 * backend's `decodeNonce` can parse it.
 */

import { base64urlDecode, base64urlEncode } from "../webauthn-flow";
import { makeSHA256 } from "./sha256";

export interface PoWInput {
  /** base64url-encoded challenge bytes from the server. */
  challenge: string;
  /** Required leading zero bits. */
  difficulty: number;
}

export interface PoWOutput {
  /** base64url-encoded nonce that satisfies the difficulty. */
  nonce: string;
  /** Number of hash attempts the solver took. Useful for UX + metrics. */
  attempts: number;
  /** Elapsed wall-clock ms. */
  elapsedMs: number;
}

/**
 * Solve a proof-of-work challenge. Returns the satisfying
 * nonce when found. Rejects if `signal` fires — the
 * caller's AbortController is the escape hatch when the
 * user navigates away during a long solve.
 *
 * The loop budget caps the number of attempts so a solver
 * stuck on a pathological challenge (server bug → too-high
 * difficulty) cannot burn the user's CPU forever. Default
 * is 10 million attempts which is ~10x the expected work
 * at difficulty 20.
 */
export async function solvePoW(
  input: PoWInput,
  opts: {
    maxAttempts?: number;
    /**
     * Wall-clock ceiling for the solve. Defaults to 30
     * seconds. On devices slow enough to hit this
     * threshold the user sees a clear "try a different
     * device" error instead of an indefinite spinner.
     */
    maxWallClockMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<PoWOutput> {
  const { challenge, difficulty } = input;
  const maxAttempts = opts.maxAttempts ?? 10_000_000;
  const maxWallClockMs = opts.maxWallClockMs ?? 30_000;

  const challengeBytes = base64urlDecode(challenge);

  // 8-byte big-endian nonce counter with a random start
  // so two solvers on the same challenge do not collide.
  const nonceBytes = new Uint8Array(8);
  crypto.getRandomValues(nonceBytes);

  const startedAt = performance.now();
  let attempts = 0;

  // Preallocate the input buffer once per call: challenge
  // bytes + 8-byte nonce. Each iteration rewrites the last
  // 8 bytes and hashes the whole thing.
  const buf = new Uint8Array(challengeBytes.length + nonceBytes.length);
  buf.set(challengeBytes, 0);

  // Output buffer — reused across every hash so the hot
  // loop allocates nothing.
  const digest = new Uint8Array(32);

  // Hash function with preallocated working buffers. See
  // sha256.ts for why we use a synchronous implementation
  // here instead of crypto.subtle.digest: for a small-input
  // PoW loop the await overhead on SubtleCrypto (~50µs per
  // call) dominates the ~1µs of actual hash work, turning a
  // sub-second solve into a 10-15 second stall. The
  // synchronous SHA-256 sacrifices ~10x raw hash throughput
  // versus a native WebCrypto call on a single large input
  // but wins 50x overall on this workload because it
  // eliminates the microtask boundary per hash.
  const hash = makeSHA256();

  // We yield to the event loop every 8192 attempts so a
  // difficult solve still lets the browser paint the
  // "solving proof-of-work" spinner. AbortSignal is checked
  // at the same cadence. 8192 is ~5–10ms of work at the
  // sync SHA-256's throughput, imperceptible to the user.
  const yieldEvery = 8192;


  while (true) {
    if (attempts >= maxAttempts) {
      throw new Error(`proof-of-work: exceeded ${maxAttempts} attempts`);
    }
    if (performance.now() - startedAt > maxWallClockMs) {
      throw new Error(
        `proof-of-work: exceeded ${maxWallClockMs}ms wall-clock budget (device too slow)`,
      );
    }
    if (opts.signal?.aborted) {
      throw new DOMException("proof-of-work: aborted", "AbortError");
    }

    buf.set(nonceBytes, challengeBytes.length);
    hash(buf, digest);
    attempts++;

    if (leadingZeroBits(digest) >= difficulty) {
      return {
        nonce: base64urlEncode(nonceBytes.buffer as ArrayBuffer),
        attempts,
        elapsedMs: performance.now() - startedAt,
      };
    }

    incrementBE(nonceBytes);

    if (attempts % yieldEvery === 0) {
      // Yield to the event loop so the browser can paint.
      await new Promise<void>((r) => setTimeout(r, 0));
    }
  }
}

/**
 * leadingZeroBits counts how many leading bits of the
 * input byte array are zero. Mirrors the server's
 * leadingZeroBits function byte-for-byte so both sides
 * agree on the difficulty threshold.
 */
export function leadingZeroBits(b: Uint8Array): number {
  let count = 0;
  for (let i = 0; i < b.length; i++) {
    const by = b[i] ?? 0;
    if (by === 0) {
      count += 8;
      continue;
    }
    for (let bit = 7; bit >= 0; bit--) {
      if ((by & (1 << bit)) !== 0) {
        return count;
      }
      count++;
    }
    return count;
  }
  return count;
}

/** Big-endian uint64 increment on an 8-byte Uint8Array. */
function incrementBE(b: Uint8Array): void {
  for (let i = b.length - 1; i >= 0; i--) {
    const v = (b[i] ?? 0) + 1;
    if (v < 256) {
      b[i] = v;
      return;
    }
    b[i] = 0;
  }
  // Overflow — nonce space exhausted. Astronomical for
  // 2^64 but handle gracefully by starting from zero.
}
